// ==================== RN ⇄ H5 桥接兜底 shim ====================
// 用途：RN 端 BridgeWebView 通过 injectedJavaScriptBeforeContentLoaded 注入 window.RNBridge。
// 已知 Android 8 系统 WebView 上该 API 注入失效，导致 window.RNBridge 一直不存在。
// 此 shim 在 H5 侧检测到该场景时，按与原生 BRIDGE_INIT_SCRIPT 一致的协议
// （对应 RN: jellytec-app/src/components/webview/BridgeWebView.tsx）
// 自己挂一份 window.RNBridge + window.__RNBridgeReceive，走 window.ReactNativeWebView.postMessage
// 与 RN 端 onMessage 通信。RN 端无需任何改动。
//
// 协议要点：
// - H5 → RN：ReactNativeWebView.postMessage(JSON.stringify({ channel: 'rn_bridge', type: 'request', requestId, method, params }))
// - RN → H5：injectJavaScript 调 window.__RNBridgeReceive(JSON.stringify(message))
//   可能是 response（兑现 invokeNative 的 callback）或 request（RN 主动 callH5）
// - 安装完成后发一条 type: 'ready' 让 RN 端 flushQueuedMessages 把先前 queue 的消息发出来

import type { BridgeResponse, RNBridge } from '@/utils/bridge'

type BridgeRawMessage = {
  channel?: string
  type?: 'request' | 'response' | 'ready'
  requestId?: string
  method?: string
  params?: unknown
  success?: boolean
  data?: unknown
  error?: string
}

type RNWebViewLike = {
  postMessage: (msg: string) => void
}

type RNBridgeWindow = Window & {
  RNBridge?: RNBridge
  ReactNativeWebView?: RNWebViewLike
  __RNBridgeReceive?: (raw: string | BridgeRawMessage) => void
  __H5_BRIDGE_SHIM_INSTALLED__?: boolean
  __RN_BRIDGE_INSTALLED__?: boolean
}

const SHIM_LOG_PREFIX = '[H5BridgeShim]'

function shimLog(action: string, detail?: unknown): void {
  if (detail === undefined) {
    console.log(SHIM_LOG_PREFIX, action)
  } else {
    console.log(SHIM_LOG_PREFIX, action, detail)
  }
}

/**
 * 安装 RNBridge shim。
 * - window.RNBridge 已存在（原生注入成功）→ 补发 ready 后 return false，不替换桥
 * - window.ReactNativeWebView.postMessage 不存在（浏览器预览）→ return false，降级行为不变
 * - 都不满足：挂 shim，挂完发一条 ready 消息让 RN 端 flush 队列 → return true
 */
export function installRNBridgeShim(): boolean {
  if (typeof window === 'undefined') return false
  const w = window as RNBridgeWindow

  // documentStart 的首条 ready 可能早于原生消息通道就绪；H5 启动时再通知一次。
  const announceReady = () => w.ReactNativeWebView?.postMessage(
    JSON.stringify({ channel: 'rn_bridge', type: 'ready' }),
  )

  if (w.__H5_BRIDGE_SHIM_INSTALLED__) {
    announceReady()
    return true
  }
  if (w.RNBridge && typeof w.RNBridge.invokeNative === 'function') {
    announceReady()
    return false
  }
  if (!w.ReactNativeWebView || typeof w.ReactNativeWebView.postMessage !== 'function') {
    return false
  }

  const pending = new Map<string, (resp: BridgeResponse) => void>()
  const handlers = new Map<string, (params: unknown) => unknown | Promise<unknown>>()
  let seq = 0

  function safePost(msg: BridgeRawMessage): void {
    w.ReactNativeWebView?.postMessage(JSON.stringify(msg))
  }

  function sendResponse(
    requestId: string,
    success: boolean,
    data?: unknown,
    error?: string,
  ): void {
    safePost({
      channel: 'rn_bridge',
      type: 'response',
      requestId,
      success,
      data,
      error,
    })
  }

  function normalizeError(err: unknown): string {
    if (!err) return 'Unknown error'
    if (typeof err === 'string') return err
    if (err instanceof Error) return err.message
    try {
      return JSON.stringify(err)
    } catch {
      return 'Unknown error'
    }
  }

  // RN 端会通过 injectJavaScript 调 window.__RNBridgeReceive(JSON.stringify(...))
  w.__RNBridgeReceive = (raw: string | BridgeRawMessage) => {
    let msg: BridgeRawMessage | null = null
    try {
      msg = typeof raw === 'string' ? (JSON.parse(raw) as BridgeRawMessage) : raw
    } catch {
      return
    }
    if (!msg || msg.channel !== 'rn_bridge') return

    // 响应：兑现 invokeNative 的 callback
    if (msg.type === 'response' && msg.requestId) {
      const cb = pending.get(msg.requestId)
      if (!cb) return
      pending.delete(msg.requestId)
      cb({
        success: msg.success,
        data: msg.data,
        error: msg.error,
      })
      return
    }

    // 请求：RN 主动 callH5，命中 H5 registerHandler 注册的 handler
    if (msg.type === 'request' && msg.requestId && msg.method) {
      const requestId = msg.requestId
      const method = msg.method
      const params = msg.params
      const handler = handlers.get(method)
      if (!handler) {
        sendResponse(requestId, false, undefined, `H5 handler not found: ${method}`)
        return
      }
      Promise.resolve()
        .then(() => handler(params))
        .then((result) => sendResponse(requestId, true, result))
        .catch((error) => sendResponse(requestId, false, undefined, normalizeError(error)))
    }
  }

  const bridge: RNBridge = {
    invokeNative(
      method: string,
      params: unknown,
      callback?: (resp: BridgeResponse) => void,
    ): string {
      const requestId = `h5_${Date.now()}_${++seq}`
      if (typeof callback === 'function') {
        pending.set(requestId, callback)
      }
      safePost({
        channel: 'rn_bridge',
        type: 'request',
        requestId,
        method,
        params,
      })
      return requestId
    },
    registerHandler(name: string, fn: (params: unknown) => unknown): void {
      handlers.set(name, fn)
    },
    unregisterHandler(name: string): void {
      handlers.delete(name)
    },
  }

  w.RNBridge = bridge
  w.__H5_BRIDGE_SHIM_INSTALLED__ = true
  // 与 App 注入脚本共用安装标记，防止 Android 迟到注入重建 pending/handlers，
  // 从而吞掉已经发出的登录回调和登录状态推送监听。
  w.__RN_BRIDGE_INSTALLED__ = true

  shimLog('shim installed (Android 8 / injectedJavaScriptBeforeContentLoaded fallback)')

  // 让 RN 端 flushQueuedMessages 把早前 queue 的消息投递过来
  safePost({ channel: 'rn_bridge', type: 'ready' })

  return true
}
